import { useState } from 'react'
import type { CommonCode, Region } from '@/shared/lib/apiTypes'
import { figmaLabel, sortByFigma } from '@/shared/lib/figmaLabel'
import { Selectbutton } from '@/shared/ui/Selectbutton'
import { ModalPc } from '@/shared/ui/ModalPc'
import { EMPTY_FILTER, type FilterValue } from '@/shared/lib/filterValue'

type Tab = 'category' | 'target' | 'region'

const TABS: { key: Tab; label: string }[] = [
  { key: 'category', label: '문제 분야' },
  { key: 'target', label: '주 대상' },
  { key: 'region', label: '지역' },
]

export function FilterModalPc({
  value,
  categories,
  lifecycle,
  situation,
  dongs,
  onApply,
  onClose,
  label,
}: {
  value: FilterValue
  categories: CommonCode[]
  lifecycle: CommonCode[]
  situation: CommonCode[]
  dongs: Region[]
  onApply: (next: FilterValue) => void
  onClose: () => void
  label: string
}) {
  const [tab, setTab] = useState<Tab>('category')
  const [draft, setDraft] = useState<FilterValue>(value)

  const toggle = (axis: keyof FilterValue, code: string) => {
    setDraft((d) => ({
      ...d,
      [axis]: d[axis].includes(code) ? d[axis].filter((c) => c !== code) : [...d[axis], code],
    }))
  }

  return (
    <ModalPc
      title="필터"
      label={label}
      onClose={onClose}
      footer={
        <>
          <button
            type="button"
            className="ui-pressable h-14 w-40 shrink-0 cursor-pointer rounded-2xl border border-white-500 bg-white-50 px-[22px] py-3 text-body-lg text-black-600"
            onClick={() => setDraft(EMPTY_FILTER)}
          >
            초기화
          </button>
          <button
            type="button"
            className="ui-pressable h-14 min-w-0 flex-1 shrink-0 grow basis-0 cursor-pointer rounded-2xl border-0 bg-violet-500 px-4 py-3 text-body-lg font-medium text-white-50"
            onClick={() => {
              onApply(draft)
              onClose()
            }}
          >
            적용하기
          </button>
        </>
      }
    >
      <div className="flex min-h-0 flex-1 flex-col gap-3">
        <div className="flex h-10 w-[375px] max-w-full shrink-0 items-end gap-2" role="tablist">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={tab === t.key}
              className={
                tab === t.key
                  ? 'ui-pressable min-w-0 flex-1 shrink-0 grow basis-0 cursor-pointer border-0 border-b-2 border-b-violet-500 bg-none px-4 py-[9px] text-body-md font-semibold text-[#2a303c]'
                  : 'ui-pressable min-w-0 flex-1 shrink-0 grow basis-0 cursor-pointer border-0 border-b-2 border-b-transparent bg-none px-4 py-[9px] text-body-md text-black-100'
              }
              onClick={() => setTab(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto">
          {tab === 'category' && (
              <ChipGrid
                items={sortByFigma(categories).map((c) => ({
                  code: c.code,
                  label: figmaLabel(c.code, c.label),
                }))}
                selected={draft.categories}
                onToggle={(code) => toggle('categories', code)}
              />
            )}

            {tab === 'target' && (
              <ChipGrid
                items={[...lifecycle, ...situation].map((c) => ({
                  code: c.code,
                  label: figmaLabel(c.code, c.label),
                }))}
                selected={draft.targets}
                onToggle={(code) => toggle('targets', code)}
              />
            )}

            {tab === 'region' && (
              <ChipGrid
                items={dongs.map((r) => ({ code: r.code, label: r.name }))}
                selected={draft.regions}
                onToggle={(code) => toggle('regions', code)}
              />
            )}
        </div>
      </div>
    </ModalPc>
  )
}

function ChipGrid({
  items,
  selected,
  onToggle,
}: {
  items: { code: string; label: string }[]
  selected: string[]
  onToggle: (code: string) => void
}) {
  return (
    <div className="grid grid-cols-3 gap-3">
      {items.map((it) => (
        <Selectbutton
          key={it.code}
          label={it.label}
          isOn={selected.includes(it.code)}
          size="pc"
          onSelect={() => onToggle(it.code)}
        />
      ))}
    </div>
  )
}
